(() => {
  'use strict';
  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const init = root => {
    if (!root || root.dataset.hghMotionReady) return;
    const track = root.querySelector('[data-hgh-cinema]');
    const pin = track?.querySelector('[data-hgh-cinema-pin]');
    const chapters = track ? [...track.querySelectorAll('[data-hgh-chapter]')].sort((a, b) => Number(a.dataset.hghChapter) - Number(b.dataset.hghChapter)) : [];
    if (!track || !pin || !track.querySelector('.hgh-cinema-pack') || chapters.length !== 3 ||
        chapters.some((chapter, index) => Number(chapter.dataset.hghChapter) !== index)) return;
    root.dataset.hghMotionReady = 'true';
    const buttons = [...root.querySelectorAll('[data-hgh-chapter-button]')];
    const toggles = [...root.querySelectorAll('[data-hgh-motion-toggle]')];
    const status = root.querySelector('[data-hgh-chapter-status]');
    const ingredient = root.querySelector('[data-hgh-ingredient-current]');
    const copy = root.querySelector('.hgh-manifesto h2');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const fine = matchMedia('(hover: hover) and (pointer: fine)');
    const cleanups = [];
    const original = chapters.map(chapter => ({ hidden: chapter.getAttribute('aria-hidden'), inert: chapter.hasAttribute('inert') }));
    const listen = (target, event, fn, options) => {
      target.addEventListener(event, fn, options);
      cleanups.push(() => target.removeEventListener(event, fn, options));
    };
    let enabled = false, paused = false, disposed = false, frame = 0, lastTime = 0;
    let trackVisible = true, copyVisible = !!copy, dirty = true, active = -1;
    let progress = 0, targetProgress = 0, tiltX = 0, tiltY = 0, targetX = 0, targetY = 0;
    let copyProgress = 1, targetCopy = 1;
    let range = 0;

    const restoreChapters = () => {
      chapters.forEach((chapter, index) => {
        chapter.inert = original[index].inert;
        if (original[index].hidden === null) chapter.removeAttribute('aria-hidden');
        else chapter.setAttribute('aria-hidden', original[index].hidden);
        chapter.classList.remove('is-current');
        chapter.style.removeProperty('--chapter-opacity');
        chapter.style.removeProperty('--chapter-shift');
      });
      buttons.forEach(button => button.removeAttribute('aria-current'));
      active = -1;
    };
    const syncAmbient = () => {
      track.classList.toggle('is-in-view', enabled && trackVisible && !paused && !reduced.matches && !document.hidden);
    };
    const updateControls = () => {
      root.classList.toggle('is-motion-paused', paused);
      root.classList.toggle('is-motion-reduced', reduced.matches || paused);
      syncAmbient();
      toggles.forEach(toggle => {
        const label = reduced.matches ? 'Motion reduced' : paused ? 'Resume motion' : 'Pause motion';
        toggle.setAttribute('aria-label', label);
        toggle.setAttribute('aria-pressed', String(paused || reduced.matches));
        toggle.hidden = !enabled;
        toggle.disabled = !enabled;
        const text = toggle.querySelector('[data-hgh-motion-label], .hgh-motion-toggle-label');
        if (text) text.textContent = label;
        else if (!toggle.children.length) toggle.textContent = label;
        const icon = toggle.querySelector('.hgh-motion-toggle-icon');
        if (icon) icon.textContent = paused ? '▶' : 'Ⅱ';
      });
    };
    const readTargets = () => {
      dirty = false;
      if (enabled) {
        const bounds = track.getBoundingClientRect();
        range = Math.max(0, bounds.height - pin.getBoundingClientRect().height);
        targetProgress = range > 0 ? clamp(-bounds.top / range) : 0;
      }
      if (copy) {
        const bounds = copy.getBoundingClientRect();
        targetCopy = reduced.matches ? 1 : clamp((innerHeight * 0.88 - bounds.top) / Math.max(1, innerHeight * 0.62 + bounds.height));
      }
    };
    const paint = () => {
      track.style.setProperty('--cinema-progress', progress.toFixed(4));
      track.style.setProperty('--cinema-tilt-x', tiltX.toFixed(3));
      track.style.setProperty('--cinema-tilt-y', tiltY.toFixed(3));
      if (copy) copy.style.setProperty('--hgh-copy-progress', copyProgress.toFixed(4));
      if (!enabled) return;
      const next = Math.round(progress * 2);
      chapters.forEach((chapter, index) => {
        const distance = progress * 2 - index;
        const fade = clamp((Math.abs(distance) - 0.20) / 0.29);
        // Keep a readable plateau, then fade fully before the following headline appears.
        chapter.style.setProperty('--chapter-opacity', (1 - fade * fade * (3 - 2 * fade)).toFixed(4));
        chapter.style.setProperty('--chapter-shift', `${(-clamp(distance, -1, 1) * 34).toFixed(2)}px`);
      });
      if (next === active) return;
      const focusedChapter = chapters.find(chapter => chapter.contains(document.activeElement));
      if (focusedChapter && focusedChapter !== chapters[next]) {
        buttons.find(button => Number(button.dataset.hghChapterButton) === next)?.focus({ preventScroll: true });
      }
      active = next;
      chapters.forEach((chapter, index) => {
        chapter.classList.toggle('is-current', index === active);
        chapter.inert = index !== active;
        chapter.setAttribute('aria-hidden', String(index !== active));
      });
      buttons.forEach(button => {
        if (Number(button.dataset.hghChapterButton) === active) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      if (status) status.textContent = chapters[active].dataset.hghChapterLabel || `${String(active + 1).padStart(2, '0')} / 03`;
      if (ingredient && chapters[active].dataset.hghIngredient) ingredient.textContent = chapters[active].dataset.hghIngredient;
    };
    const cancel = () => { cancelAnimationFrame(frame); frame = 0; lastTime = 0; };
    const tick = time => {
      frame = 0;
      if (disposed || document.hidden) { lastTime = 0; return; }
      if (dirty) readTargets();
      const delta = lastTime ? clamp(time - lastTime, 1, 64) : 16;
      lastTime = time;
      const instant = paused || reduced.matches;
      const alpha = 1 - Math.exp(-delta / 270);
      const tiltAlpha = 1 - Math.exp(-delta / 150);
      progress += (targetProgress - progress) * (instant || !trackVisible ? 1 : alpha);
      tiltX += (targetX - tiltX) * (instant || !trackVisible ? 1 : tiltAlpha);
      tiltY += (targetY - tiltY) * (instant || !trackVisible ? 1 : tiltAlpha);
      copyProgress += (targetCopy - copyProgress) * (instant || !copyVisible ? 1 : alpha);
      if (Math.abs(targetProgress - progress) < 0.001) progress = targetProgress;
      if (Math.abs(targetX - tiltX) < 0.008) tiltX = targetX;
      if (Math.abs(targetY - tiltY) < 0.008) tiltY = targetY;
      if (Math.abs(targetCopy - copyProgress) < 0.001) copyProgress = targetCopy;
      paint();
      if ((trackVisible && (progress !== targetProgress || tiltX !== targetX || tiltY !== targetY)) ||
          (copyVisible && copyProgress !== targetCopy)) frame = requestAnimationFrame(tick);
      else lastTime = 0;
    };
    const schedule = () => {
      if (!disposed && !frame && !document.hidden) frame = requestAnimationFrame(tick);
    };
    const syncMode = () => {
      if (disposed) return;
      const wasEnabled = enabled;
      const stickySupported = !window.CSS?.supports || CSS.supports('position', 'sticky');
      enabled = !reduced.matches && innerWidth >= 320 && innerHeight >= 480 && stickySupported;
      root.classList.toggle('is-cinema-ready', enabled);
      if (enabled) {
        const bounds = track.getBoundingClientRect();
        const pinHeight = pin.getBoundingClientRect().height;
        trackVisible = bounds.bottom > 0 && bounds.top < innerHeight;
        if (bounds.height - pinHeight < 2 || pinHeight > innerHeight + 1) {
          enabled = false;
          root.classList.remove('is-cinema-ready');
        }
      }
      if (!enabled) {
        restoreChapters();
        progress = targetProgress = 0;
        tiltX = tiltY = targetX = targetY = 0;
      }
      dirty = true;
      readTargets();
      if (!wasEnabled || !enabled || reduced.matches) {
        progress = targetProgress;
        copyProgress = targetCopy;
      }
      updateControls();
      paint();
      schedule();
    };
    const resetTilt = () => { targetX = targetY = 0; schedule(); };
    listen(window, 'scroll', () => {
      dirty = true;
      if (trackVisible || copyVisible) schedule();
    }, { passive: true });
    listen(window, 'resize', syncMode, { passive: true });
    listen(window, 'load', syncMode, { once: true });
    listen(reduced, 'change', syncMode);
    listen(fine, 'change', resetTilt);
    listen(document, 'visibilitychange', () => {
      root.classList.toggle('is-motion-hidden', document.hidden);
      syncAmbient();
      if (document.hidden) { cancel(); targetX = targetY = 0; }
      else { dirty = true; schedule(); }
    });
    listen(pin, 'pointermove', event => {
      if (!enabled || paused || reduced.matches || !fine.matches || !trackVisible || event.pointerType === 'touch') return;
      const bounds = pin.getBoundingClientRect();
      targetX = -clamp((event.clientY - bounds.top) / Math.max(1, bounds.height) - 0.5, -0.5, 0.5) * 5;
      targetY = clamp((event.clientX - bounds.left) / Math.max(1, bounds.width) - 0.5, -0.5, 0.5) * 7;
      schedule();
    }, { passive: true });
    listen(pin, 'pointerleave', resetTilt, { passive: true });
    listen(pin, 'pointercancel', resetTilt, { passive: true });
    toggles.forEach(toggle => listen(toggle, 'click', () => {
      if (!enabled || reduced.matches) return;
      paused = !paused;
      // The ready class stays set: pausing never changes the scroll-track geometry.
      updateControls();
      targetX = targetY = 0;
      dirty = true;
      schedule();
    }));
    buttons.forEach(button => listen(button, 'click', () => {
      const index = Number(button.dataset.hghChapterButton);
      if (!Number.isInteger(index) || index < 0 || index > 2) return;
      const trackBounds = track.getBoundingClientRect();
      const distance = Math.max(0, trackBounds.height - pin.getBoundingClientRect().height);
      const top = enabled ? scrollY + trackBounds.top + distance * index / 2 : scrollY + chapters[index].getBoundingClientRect().top;
      window.scrollTo({ top: Math.max(0, top), behavior: enabled && !paused && !reduced.matches ? 'smooth' : 'auto' });
    }));
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target === track) {
            trackVisible = entry.isIntersecting;
            track.classList.toggle('is-cinema-in-view', trackVisible);
            syncAmbient();
            if (!trackVisible) targetX = targetY = 0;
          } else if (entry.target === copy) copyVisible = entry.isIntersecting;
        });
        dirty = true;
        schedule();
      });
      observer.observe(track);
      if (copy) observer.observe(copy);
      cleanups.push(() => observer.disconnect());
    }
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(syncMode);
      observer.observe(track);
      observer.observe(pin);
      cleanups.push(() => observer.disconnect());
    }
    const unload = event => {
      if (!event.target.contains(root)) return;
      disposed = true;
      cancel();
      cleanups.forEach(cleanup => cleanup());
      restoreChapters();
      root.classList.remove('is-cinema-ready', 'is-motion-reduced', 'is-motion-paused', 'is-motion-hidden');
      track.classList.remove('is-cinema-in-view', 'is-in-view');
      ['--cinema-progress', '--cinema-tilt-x', '--cinema-tilt-y'].forEach(name => track.style.removeProperty(name));
      copy?.style.removeProperty('--hgh-copy-progress');
      delete root.dataset.hghMotionReady;
      document.removeEventListener('shopify:section:unload', unload);
    };
    document.addEventListener('shopify:section:unload', unload);
    syncMode();
  };
  document.querySelectorAll('.hgh[data-gummies-home]').forEach(init);
  document.addEventListener('shopify:section:load', event => {
    if (event.target.matches?.('.hgh[data-gummies-home]')) init(event.target);
    event.target.querySelectorAll('.hgh[data-gummies-home]').forEach(init);
  });
})();
